<script>
  import { tick } from "svelte";
  import { navigate } from "svelte-routing";
  import { bucketArray } from "overline";
  import XIcon from "@local/assets/icons/x.svg";
  import FlagIcon from "@local/assets/icons/flag-filled.svg";
  import { reviewing } from "#src/stores/ui.js";
  import { isLocationVisible } from "#src/extensions/filter-items.js";

  export let group;

  let currentNoteIndex = 0;
  let currentDestIndex = 0;
  let list = []; // [{ note, destinations: [] }]
  let totalLocations = 0;

  $: currentNote = getNote(list, currentNoteIndex);
  $: next = getNote(list, currentNoteIndex + 1);
  $: prev = getNote(list, currentNoteIndex - 1);
  $: linkPrefix = `/jobs/${$group?.job_id}`;
  $: init($reviewing);

  function getNote(list, index) {
    return list[index];
  }

  function defaultFilters() {
    return {
      show_items: true,
      show_collections: true,
      show_types: true,
      show_notes: true,
      show_dimensions: true,
      location_filter: null,
      item_filter: null,
      collection_filter: null,
      type_filter: null,
      note_filter: null,
      hidden_pages: {},
      hidden_locations: {},
    };
  }

  function toggleVisible() {
    reviewing.set(null);
    currentNoteIndex = 0;
    currentDestIndex = 0;
  }

  function noteLocations() {
    return $group.locations.order
      .filter((locId) => {
        const loc = $group.locations[locId];
        return loc?.record_type === "document_note";
      })
      .map((locId) => $group.locations[locId]);
  }

  async function nextNote() {
    if (currentNoteIndex < list.length - 1) {
      currentNoteIndex += 1;
      currentDestIndex = 0;
    }
    openLink();
  }

  async function prevNote(lastDest = false) {
    if (currentNoteIndex > 0) {
      currentNoteIndex -= 1;
      const note = list[currentNoteIndex];
      if (lastDest) {
        currentDestIndex = Math.max(0, note.destinations.length - 1);
      } else {
        currentDestIndex = 0;
      }
    }
    openLink();
  }

  async function nextDest() {
    if (currentDestIndex < list[currentNoteIndex]?.destinations.length - 1) {
      currentDestIndex += 1;
    } else {
      nextNote();
    }
    openLink();
  }

  async function prevDest() {
    if (currentDestIndex > 0) {
      currentDestIndex -= 1;
    } else {
      prevNote(true);
    }
    openLink();
  }

  function getLink(noteIndex, destIndex) {
    const dest = list[noteIndex]?.destinations[destIndex];
    if (dest) {
      return `${linkPrefix}/documents/${dest.document_id}?selected-records=${dest.location_id}`;
    }
  }

  async function openLink() {
    if (!$reviewing) return;
    await tick();

    const link = getLink(currentNoteIndex, currentDestIndex);
    if (link) {
      navigate(link);
    }
  }

  function getDestination(report, loc) {
    if (report.link_destination) {
      const doc = $group.documents[report.link_destination];
      if (!doc) return null;
      if (doc.type === "virtual") {
        const filter = doc.data.filters || defaultFilters();
        if (isLocationVisible(loc, $group, filter)) {
          return {
            location_id: loc.id,
            document_id: doc.id,
            index: 0,
          };
        } else {
          return null;
        }
      } else if (doc.type === "imported") {
        if (loc.document_id === doc.id) {
          return {
            location_id: loc.id,
            document_id: doc.id,
            index: 0,
          };
        } else {
          return null;
        }
      } else {
        return null;
      }
    } else {
      // Find the first virtual document with this location visible
      const virtualDoc = $group.documents.order.find((docId) => {
        const doc = $group.documents[docId];
        if (doc?.type !== "virtual") return false;
        const filter = doc.data.filters || defaultFilters();
        return loc.document_id === doc.source_document_id && isLocationVisible(loc, $group, filter);
      });

      if (virtualDoc) {
        return {
          location_id: loc.id,
          document_id: virtualDoc,
          index: 0,
        };
      }

      const importedDoc = $group.documents.order.find((docId) => {
        const doc = $group.documents[docId];
        if (doc?.type !== "imported") return false;
        return doc.id === loc.document_id;
      });

      if (importedDoc) {
        return {
          location_id: loc.id,
          document_id: importedDoc,
          index: 0,
        };
      }

      return null;
    }
  }

  async function init(r) {
    if (!r) {
      list = [];
      currentNoteIndex = 0;
      currentDestIndex = 0;
      return;
    }

    const locs = noteLocations();
    const locsByRecord = bucketArray(locs, "record_id");
    list = r.notes
      .filter((note) => {
        return locsByRecord[note.id]?.length > 0;
      })
      .map((note) => {
        const locations = locsByRecord[note.id];

        const destinations = locations
          .map((loc) => getDestination(r.report, loc))
          .filter((dest) => dest != null);

        return { note, destinations };
      })
      .filter((n) => n.destinations.length > 0);

    let total = 0;
    let index = 0;
    list.forEach((n) => {
      n.destinations.forEach((d) => {
        d.index = index;
        index += 1;
      });
      total += n.destinations.length;
    });
    totalLocations = total;

    await tick();
    list = list;
    currentNoteIndex = 0;
    currentDestIndex = 0;

    if (list.length > 0) {
      openLink();
    }
  }
</script>

<div class="px-4 pb-12 absolute bottom-0 left-0 z-50 pointer-events-none text-xs">
  {#if $reviewing && currentNote}
    {@const note = currentNote.note}
    {@const dest = currentNote.destinations[currentDestIndex]}
    <div
      class="bg-white rounded border-2 border-blue-500 shadow-lg shadow-blue-500/50 pointer-events-auto w-64">
      <div class="p-2 flex items-center border-b">
        <div class="grow">Reviewing: {$reviewing.report.report_name}</div>
        <button on:click={toggleVisible} class="rounded p-1 hover:bg-gray-200">
          <XIcon />
        </button>
      </div>
      <div class="space-y-2">
        <div class="px-4 pt-4 flex items-center justify-between">
          <div>
            <span>{note.mark}:</span>
            <span class="font-bold">{note.title}</span>
          </div>
          {#if note.approval_status === "flagged"}
            <div class="text-red-500">
              <FlagIcon />
            </div>
          {:else if note.approval_status === "approved"}
            <div class="text-green-500">
              <FlagIcon />
            </div>
          {/if}
        </div>
        {#if note.body}
          <div class="px-4">{note.body}</div>
        {/if}
        <div class="p-2">
          <!-- {#if currentNote.destinations.length > 1}
            <div class="flex items-center gap-2">
              <button class="btn-text" disabled={currentDestIndex === 0 && !prev} on:click={() => prevDest()}
                ><CaretLeft /></button>
              <div>
                {currentDestIndex + 1} of {currentNote.destinations.length}
              </div>
              <button
                class="btn-text"
                disabled={currentDestIndex === currentNote.destinations.length - 1 && !next}
                on:click={() => nextDest()}><CaretRight /></button>
            </div>
          {/if} -->
          <div class="flex justify-between items-center">
            <button class="btn-text" disabled={currentDestIndex === 0 && !prev} on:click={() => prevDest()}
              >Prev</button>
            <div>
              {list.length > 0 ? `${dest.index + 1} of ${totalLocations}` : "No notes to review"}
            </div>
            <button
              class="btn-text"
              disabled={currentDestIndex === currentNote.destinations.length - 1 && !next}
              on:click={() => nextDest()}>Next</button>
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>
